import * as React from 'react';
import {
  MISAFIR_TALEPLERI,
  TALEP_ADIMI,
  paraEtiketi,
  secenekBul,
  talepMetni,
  type MisafirTalepDurum,
  type MisafirTalepTipi,
  type TalepDili,
} from '@hotelcms/shared';
import { ApiHatasi, api, misafirOturum } from '@/lib/api';
import { tarihFormatla } from '@/lib/utils';
import { aktifDil, ceviri } from '@/locales';

/**
 * Misafir mobil ekrani - odadaki QR.
 *
 * Tasarim kaynagi: Claude Design - "otelcms Misafir Mobil.dc.html".
 *
 * Tasarim iki telefonu yan yana gosteriyordu; bu bir SUNUM bicimiydi (soldaki
 * telefonda talep ac, sagdakinde izle). Gercek misafirin tek telefonu var, o
 * yuzden burada tek uygulama ve iki gorunum vardir: "Ana ekran" ve
 * "Taleplerim". Talep gonderilince ekran kendiliginden takibe gecer.
 * Tasarimdaki iOS cercevesi (ios-frame.jsx) de bir sunum aracidir: burada
 * cerceve zaten misafirin kendi telefonudur.
 *
 * Veri:
 *   GET  /api/public/oda/:odaNo                     -> karsilama, Wi-Fi, talepler
 *   POST /api/public/oda/:odaNo/talepler            -> talep ac
 *   POST /api/public/oda/:odaNo/talepler/:id/iptal  -> geri al
 *   POST /api/public/oda/:odaNo/talepler/:id/puan   -> yildiz ver
 *
 * KIMLIK KAPISI: QR okutulunca once kimlik numarasi sorulur. Numara o odada
 * su an konaklayan birine aitse oturum acilir ve menu gorunur; degilse
 * "yanlis numara" ile "bos oda" ayni cevabi verir. Oda numarasini bilmek tek
 * basina yetmez - bu ekranin en onemli kurali budur.
 *
 * DIL: misafir cogunlukla yabancidir. Ekran tarayici diline gore acilir,
 * ustteki dugmeyle degistirilir; talep metinleri paylasilan katalogdan gelir.
 */

interface Talep {
  id: string;
  tip: MisafirTalepTipi;
  /** Katalog KODU - etiket istemcide, secili dilde cozulur */
  secenek: string;
  durum: MisafirTalepDurum;
  ucretKurus: number;
  puan: number | null;
  an: number;
}

interface OdaDurumu {
  otelAd: string;
  odaNo: string;
  misafirAd: string | null;
  cikisTarihi: string;
  gecCikisSaati: string | null;
  rahatsizEtmeyin: number | null;
  wifi: { ad: string; sifre: string | null } | null;
  whatsapp: string | null;
  talepler: Talep[];
}

/**
 * Talep tipinin rengi. Katalogda DEGIL cunku bu tamamen sunum: ayni katalog
 * personel ekraninda baska bir renk duzeniyle gosterilir.
 */
const RENK: Record<MisafirTalepTipi, { zemin: string; yazi: string }> = {
  TEMIZLIK: { zemin: '#edf2ff', yazi: '#1f4fd8' },
  HAVLU: { zemin: '#e6f7ec', yazi: '#0f7a3d' },
  SERVIS: { zemin: '#fff5dc', yazi: '#b9840f' },
  GEC_CIKIS: { zemin: '#f3e8ff', yazi: '#7c3aed' },
  RAHATSIZ_ETMEYIN: { zemin: '#eef1f8', yazi: '#5b6480' },
  ARIZA: { zemin: '#fdeceb', yazi: '#c0392b' },
};

const DURUM_HAP: Record<MisafirTalepDurum, { zemin: string; yazi: string }> = {
  ALINDI: { zemin: '#edf2ff', yazi: '#1f4fd8' },
  ISLEMDE: { zemin: '#fff5dc', yazi: '#b9840f' },
  TAMAMLANDI: { zemin: '#e6f7ec', yazi: '#0f7a3d' },
  IPTAL: { zemin: '#eef1f8', yazi: '#5b6480' },
};

const TIPLER = Object.keys(MISAFIR_TALEPLERI) as MisafirTalepTipi[];

const saat = (an: number) => new Date(an).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

/**
 * Misafir adlari veritabaninda BUYUK HARF durur: KBS bildirimi oyle ister.
 * Karsilama ekraninda oyle gostermek misafire bagirmak olur ("HOŞ GELDİNİZ,
 * ELİF"), tasarim da "Elif" diyor. Turkce'ye duyarli kucultme sart:
 * toLowerCase() "ELİF" -> "elIf" uretir, 'tr' yerel ayari "elif" uretir.
 */
function adiDuzelt(ad: string): string {
  return ad
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0]!.toLocaleUpperCase('tr') + p.slice(1).toLocaleLowerCase('tr'))
    .join(' ');
}

/** "Ayşe Kaya" -> "AK"; tek adliysa tek harf. */
function basHarfler(ad: string): string {
  return ad
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toLocaleUpperCase('tr') ?? '')
    .join('');
}

function Ikon({ yol, boyut = 20 }: { yol: string; boyut?: number }) {
  return (
    <svg
      width={boyut}
      height={boyut}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={yol} />
    </svg>
  );
}

export function MisafirMobil({ odaNo }: { odaNo: string }) {
  const [dil, setDil] = React.useState<TalepDili>(aktifDil());
  const t = React.useMemo(() => ceviri(dil), [dil]);

  const [veri, setVeri] = React.useState<OdaDurumu | null>(null);
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [kapali, setKapali] = React.useState(false);
  const [kapi, setKapi] = React.useState<{ otelAd: string } | null>(null);
  const [hata, setHata] = React.useState<string | null>(null);
  const [gorunum, setGorunum] = React.useState<'ana' | 'talepler'>('ana');
  const [acikTip, setAcikTip] = React.useState<MisafirTalepTipi | null>(null);
  const [secenek, setSecenek] = React.useState(0);
  const [gonderiliyor, setGonderiliyor] = React.useState(false);
  const [toast, setToast] = React.useState<string | null>(null);
  const [wifiKopyalandi, setWifiKopyalandi] = React.useState(false);

  const kok = `/api/public/oda/${encodeURIComponent(odaNo)}`;

  /** Kimlik ekranina duser: oturumu atar ve kapiyi yukler. */
  const kapiyaDus = React.useCallback(async () => {
    misafirOturum.temizle();
    setVeri(null);
    try {
      setKapi(await api<{ otelAd: string }>(`${kok}/kapi`));
      setHata(null);
    } catch (e) {
      // 404 = tesis misafir mobili acmamis. Ayri ekran.
      if (e instanceof ApiHatasi && e.status === 404) setKapali(true);
      else setHata(e instanceof ApiHatasi ? e.message : t('baglanilamadi'));
    }
  }, [kok, t]);

  const yukle = React.useCallback(async () => {
    // Oturum yoksa sunucuyu bosuna yormayalim; dogrudan kimlik ekrani.
    if (!misafirOturum.oku()) {
      await kapiyaDus();
      setYukleniyor(false);
      return;
    }
    try {
      setVeri(await api<OdaDurumu>(kok));
      setKapi(null);
      setHata(null);
    } catch (e) {
      if (e instanceof ApiHatasi && e.status === 401) {
        // Oturum dustu (sure doldu ya da misafir cikis yapti).
        await kapiyaDus();
      } else if (e instanceof ApiHatasi && e.status === 404) {
        setKapali(true);
      } else {
        setHata(e instanceof ApiHatasi ? e.message : t('baglanilamadi'));
      }
    } finally {
      setYukleniyor(false);
    }
  }, [kok, t, kapiyaDus]);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  /*
   * Personel talebi ilerletince misafir gorsun.
   * Misafir tarafinda WebSocket YOK: oturumu yok, tesisin tum yayinini
   * dinlemesi de dogru olmaz. Ekran acikken 20 saniyede bir yoklamak
   * (polling) bu is icin fazlasiyla yeterli ve cok daha ucuz.
   */
  React.useEffect(() => {
    if (kapali) return;
    const z = setInterval(() => {
      if (document.visibilityState === 'visible') void yukle();
    }, 20_000);
    return () => clearInterval(z);
  }, [kapali, yukle]);

  React.useEffect(() => {
    if (!toast) return;
    const z = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(z);
  }, [toast]);

  function dilDegistir(yeni: TalepDili) {
    setDil(yeni);
    // Kalici tercih; dilAyarla sayfayi yeniler, burada yenilemeden uyguluyoruz.
    try {
      localStorage.setItem('hotelcms.dil', yeni);
    } catch {
      /* gizli sekmede depolama kapali olabilir */
    }
  }

  async function wifiKopyala(sifre: string) {
    try {
      await navigator.clipboard.writeText(sifre);
      setWifiKopyalandi(true);
      setTimeout(() => setWifiKopyalandi(false), 1500);
    } catch {
      /* pano izni yoksa sifre zaten ekranda yaziyor */
    }
  }

  async function gonder() {
    if (!acikTip || gonderiliyor) return;
    const tanim = MISAFIR_TALEPLERI[acikTip];
    const secili = tanim.secenekler[secenek]!;
    setGonderiliyor(true);
    try {
      const cevap = await api<{ zatenAcik: boolean; talepler: Talep[] }>(`${kok}/talepler`, {
        method: 'POST',
        body: JSON.stringify({ tip: acikTip, secenek: secili.kod }),
        // Otel wi-fi'i kopabilir; talep kaybolmasin, baglanti gelince gitsin.
        cevrimdisiKuyrukla: true,
      });
      setVeri((o) => (o ? { ...o, talepler: cevap.talepler } : o));
      setToast(cevap.zatenAcik ? t('talebinizZatenAcik') : t('talebinizAlindi'));
      setAcikTip(null);
      // Tasarimin anlattigi akis: gonder, sonra ilerlemeyi izle.
      setGorunum('talepler');
    } catch (e) {
      if (e instanceof ApiHatasi && e.kod === 'CEVRIMDISI') {
        setToast(t('cevrimdisiKuyrukta'));
        setAcikTip(null);
      } else {
        setToast(e instanceof ApiHatasi ? e.message : t('gonderilemedi'));
      }
    } finally {
      setGonderiliyor(false);
    }
  }

  async function iptalEt(talepId: string) {
    try {
      const cevap = await api<{ talepler: Talep[] }>(`${kok}/talepler/${talepId}/iptal`, { method: 'POST' });
      setVeri((o) => (o ? { ...o, talepler: cevap.talepler } : o));
    } catch (e) {
      setToast(e instanceof ApiHatasi ? e.message : t('gonderilemedi'));
    }
  }

  async function puanla(talepId: string, puan: number) {
    try {
      const cevap = await api<{ talepler: Talep[] }>(`${kok}/talepler/${talepId}/puan`, {
        method: 'POST',
        body: JSON.stringify({ puan }),
      });
      setVeri((o) => (o ? { ...o, talepler: cevap.talepler } : o));
    } catch {
      /* puan kritik degil; sessizce gec */
    }
  }

  if (yukleniyor) {
    return (
      <div className="grid min-h-dvh place-items-center bg-zemin p-6 text-gri">
        <span className="text-sm">{t('yukleniyor')}</span>
      </div>
    );
  }

  if (kapali) return <LinkKapali t={t} />;

  if (kapi) {
    return (
      <KimlikKapisi
        otelAd={kapi.otelAd}
        odaNo={odaNo}
        kok={kok}
        dil={dil}
        t={t}
        onDilDegis={dilDegistir}
        onAcildi={async () => {
          setYukleniyor(true);
          setKapi(null);
          await yukle();
        }}
      />
    );
  }

  if (!veri) {
    return (
      <div className="grid min-h-dvh place-items-center bg-zemin p-6">
        <div className="max-w-[34ch] rounded-[20px] border border-cizgi bg-white p-6 text-center">
          <p className="text-sm leading-relaxed text-gri">{hata ?? t('baglanilamadi')}</p>
          <button
            onClick={() => {
              setYukleniyor(true);
              void yukle();
            }}
            className="mt-4 rounded-full bg-marka-600 px-5 py-2.5 text-sm font-semibold text-white"
          >
            {t('tekrarDene')}
          </button>
        </div>
      </div>
    );
  }

  const acikTanim = acikTip ? MISAFIR_TALEPLERI[acikTip] : null;
  const acikMetin = acikTip ? talepMetni(acikTip, dil) : null;
  const acikOlanlar = new Set(
    veri.talepler.filter((x) => x.durum === 'ALINDI' || x.durum === 'ISLEMDE').map((x) => x.tip),
  );

  return (
    <div className="relative mx-auto flex min-h-dvh max-w-[520px] flex-col gap-4 bg-zemin px-4 pb-10 pt-5 text-lacivert">
      {gorunum === 'ana' ? (
        <>
          {/* --- karsilama: tasarimdaki lacivert kart, izgara dokusu ve dalga */}
          <section className="relative overflow-hidden rounded-3xl bg-lacivert px-5 py-[22px] text-white">
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.05) 1px,transparent 1px)',
                backgroundSize: '22px 22px',
              }}
            />
            <div className="relative flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-xs font-bold uppercase tracking-[.1em] text-mavi-acik">
                  {veri.otelAd}
                </span>
                <span className="truncate text-[22px] font-bold tracking-[-.02em]">
                  {veri.misafirAd ? `${t('hosGeldiniz')}, ${adiDuzelt(veri.misafirAd)}` : t('hosGeldiniz')}
                </span>
              </div>
              {veri.misafirAd && (
                <span className="grid size-10 flex-none place-items-center rounded-full bg-marka-600 font-bold">
                  {basHarfler(veri.misafirAd)}
                </span>
              )}
            </div>
            <div className="relative mt-3.5 flex gap-2.5">
              <div className="flex flex-1 flex-col gap-0.5 rounded-2xl bg-white/10 p-3">
                <span className="text-[11px] text-[#b6c2e2]">{t('oda')}</span>
                <span className="text-xl font-bold">{veri.odaNo}</span>
              </div>
              <div className="flex flex-1 flex-col gap-0.5 rounded-2xl bg-white/10 p-3">
                <span className="text-[11px] text-[#b6c2e2]">{t('cikis')}</span>
                <span className="text-xl font-bold">
                  {tarihFormatla(veri.cikisTarihi)}
                  {veri.gecCikisSaati && (
                    <span className="ml-1.5 text-xs font-semibold text-[#5fd48f]">{veri.gecCikisSaati}</span>
                  )}
                </span>
              </div>
            </div>
            <svg
              viewBox="0 0 300 40"
              width="100%"
              className="pointer-events-none absolute -bottom-1.5 left-0 opacity-50"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M0 24q25-12 50 0t50 0 50 0 50 0 50 0 50 0 50 0"
                stroke="#8fb0ff"
                strokeWidth="2"
                className="[animation:wave_4s_ease-in-out_infinite]"
              />
            </svg>
          </section>

          {veri.rahatsizEtmeyin && (
            <div className="flex items-center gap-2.5 rounded-2xl border border-[#dfe6f5] bg-satir px-4 py-3 text-[13px] font-semibold text-gri">
              <Ikon yol={MISAFIR_TALEPLERI.RAHATSIZ_ETMEYIN.ikon} boyut={18} />
              {t('rahatsizEtmeyinAcik')}
            </div>
          )}

          {veri.wifi && (
            <section className="flex items-center gap-3.5 rounded-[20px] border border-cizgi bg-white p-4">
              <span className="grid size-11 flex-none place-items-center rounded-[14px] bg-mavi-zemin text-marka-600">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12.5a10 10 0 0114 0M8.5 16a5 5 0 017 0M2 9a14 14 0 0120 0" />
                  <circle cx="12" cy="19.5" r="1" fill="currentColor" />
                </svg>
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-xs text-gri">Wi-Fi</span>
                <span className="truncate text-[15px] font-bold">{veri.wifi.ad}</span>
                {veri.wifi.sifre && (
                  <span className="truncate text-xs text-gri">
                    {t('sifre')}: <strong className="text-lacivert">{veri.wifi.sifre}</strong>
                  </span>
                )}
              </div>
              {veri.wifi.sifre && (
                <button
                  onClick={() => void wifiKopyala(veri.wifi!.sifre!)}
                  className="whitespace-nowrap rounded-full border-[1.5px] border-lacivert px-3 py-2 text-xs font-semibold"
                >
                  {wifiKopyalandi ? t('kopyalandi') : t('kopyala')}
                </button>
              )}
            </section>
          )}

          <span className="mt-1 text-xs font-bold uppercase tracking-[.08em] text-gri">{t('neLazim')}</span>
          <div className="grid grid-cols-2 gap-2.5">
            {TIPLER.map((tip) => {
              const tanim = MISAFIR_TALEPLERI[tip];
              const metin = talepMetni(tip, dil);
              const aktif = acikOlanlar.has(tip);
              return (
                <button
                  key={tip}
                  onClick={() => {
                    setAcikTip(tip);
                    setSecenek(0);
                  }}
                  className={
                    'relative flex min-h-11 flex-col gap-2.5 rounded-[18px] border p-3.5 text-left transition-transform active:scale-[.98] ' +
                    (aktif ? 'border-[#f2d99a] bg-[#fff5dc]' : 'border-cizgi bg-white')
                  }
                >
                  <span
                    className="grid size-[38px] place-items-center rounded-xl"
                    style={{ background: RENK[tip].zemin, color: RENK[tip].yazi }}
                  >
                    <Ikon yol={tanim.ikon} />
                  </span>
                  <span className="flex flex-col gap-px">
                    <span className="text-sm font-bold">{metin.baslik}</span>
                    <span className="text-[11.5px] text-gri">{metin.altBaslik}</span>
                  </span>
                  {aktif && (
                    <span className="absolute right-3 top-3 size-2 rounded-full bg-sari [animation:blink_1.2s_infinite]" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <TalepListesi
          talepler={veri.talepler}
          odaNo={veri.odaNo}
          whatsapp={veri.whatsapp}
          dil={dil}
          t={t}
          onPuan={puanla}
          onIptal={iptalEt}
        />
      )}

      {/* --- gorunum secici + dil */}
      <nav className="sticky bottom-3 mt-auto flex items-center gap-1 self-center rounded-full border border-cizgi bg-white/95 p-1 shadow-[0_10px_30px_-12px_rgba(11,21,48,.35)] backdrop-blur">
        {(
          [
            ['ana', t('anaEkran')],
            ['talepler', t('taleplerim')],
          ] as const
        ).map(([k, etiket]) => (
          <button
            key={k}
            onClick={() => setGorunum(k as 'ana' | 'talepler')}
            className={
              'flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ' +
              (gorunum === k ? 'bg-lacivert text-white' : 'text-gri')
            }
          >
            {etiket}
            {k === 'talepler' && acikOlanlar.size > 0 && (
              <span className="grid min-w-5 place-items-center rounded-full bg-sari px-1.5 text-[11px] font-bold text-lacivert">
                {acikOlanlar.size}
              </span>
            )}
          </button>
        ))}
        <button
          onClick={() => dilDegistir(dil === 'tr' ? 'en' : 'tr')}
          aria-label={dil === 'tr' ? 'Switch to English' : "Türkçe'ye geç"}
          className="ml-0.5 rounded-full px-3 py-2.5 text-sm font-bold text-gri"
        >
          {dil === 'tr' ? 'EN' : 'TR'}
        </button>
      </nav>

      {/* --- alt sayfa (bottom sheet) */}
      {acikTanim && acikMetin && acikTip && (
        <>
          <button
            aria-label={t('vazgec')}
            onClick={() => setAcikTip(null)}
            className="fixed inset-0 z-40 bg-lacivert/45"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label={acikMetin.baslik}
            className="fixed inset-x-0 bottom-0 z-50 mx-auto flex max-w-[520px] flex-col gap-3.5 rounded-t-[28px] bg-white px-5 pb-11 pt-3.5 [animation:rise_.3s_cubic-bezier(.2,.8,.2,1)_both]"
          >
            <span className="h-1 w-10 self-center rounded-sm bg-[#dfe6f5]" />
            <div className="flex items-center gap-3">
              <span
                className="grid size-11 flex-none place-items-center rounded-[14px]"
                style={{ background: RENK[acikTip].zemin, color: RENK[acikTip].yazi }}
              >
                <Ikon yol={acikTanim.ikon} boyut={22} />
              </span>
              <div className="flex min-w-0 flex-col">
                <span className="text-lg font-bold">{acikMetin.baslik}</span>
                <span className="text-[12.5px] text-gri">{acikMetin.aciklama}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {acikTanim.secenekler.map((o, i) => (
                <button
                  key={o.kod}
                  onClick={() => setSecenek(i)}
                  aria-pressed={secenek === i}
                  className={
                    'rounded-full border-[1.5px] px-3.5 py-2.5 text-[13px] font-semibold transition-colors ' +
                    (secenek === i
                      ? 'border-lacivert bg-lacivert text-white'
                      : 'border-[#dfe6f5] bg-white text-lacivert')
                  }
                >
                  {o[dil]}
                  {o.ucretKurus ? (
                    <span className={secenek === i ? 'ml-1.5 opacity-80' : 'ml-1.5 text-gri'}>
                      {paraEtiketi(o.ucretKurus)}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
            <button
              onClick={() => void gonder()}
              disabled={gonderiliyor}
              className="mt-1 rounded-full bg-marka-600 px-4 py-[15px] text-[15px] font-bold text-white disabled:opacity-50"
            >
              {gonderiliyor ? t('gonderiliyor') : acikMetin.eylem}
            </button>
            <span className="text-center text-xs text-[#8a93ad]">{t('talepAnindaDuser')}</span>
          </div>
        </>
      )}

      {toast && (
        <div
          role="status"
          className="fixed inset-x-4 bottom-20 z-[60] mx-auto flex max-w-[480px] items-center gap-2.5 rounded-2xl bg-lacivert px-4 py-3 text-[13.5px] font-semibold text-white shadow-[0_20px_40px_-20px_rgba(11,21,48,.6)] [animation:toast_2.6s_ease-in-out_forwards]"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#5fd48f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="9" cy="9" r="7.5" />
            <path d="M5.5 9l2.5 2.5 4.5-5" />
          </svg>
          {toast}
        </div>
      )}
    </div>
  );
}

type Ceviri = ReturnType<typeof ceviri>;

/**
 * Kimlik kapisi.
 *
 * Ekranda oda hakkinda HICBIR bilgi yoktur - otelin adi disinda. Yanlis
 * numara ile bos oda ayni hatayi dondugu icin bu ekran, odalari tarayan
 * birine hicbir sey soylemez.
 */
function KimlikKapisi({
  otelAd,
  odaNo,
  kok,
  dil,
  t,
  onDilDegis,
  onAcildi,
}: {
  otelAd: string;
  odaNo: string;
  kok: string;
  dil: TalepDili;
  t: Ceviri;
  onDilDegis: (d: TalepDili) => void;
  onAcildi: () => void | Promise<void>;
}) {
  const [kimlikNo, setKimlikNo] = React.useState('');
  const [gonderiliyor, setGonderiliyor] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);

  async function dogrula(e: React.FormEvent) {
    e.preventDefault();
    if (gonderiliyor || kimlikNo.trim().length < 5) return;
    setGonderiliyor(true);
    setHata(null);
    try {
      const cevap = await api<{ oturum: string }>(`${kok}/dogrula`, {
        method: 'POST',
        body: JSON.stringify({ kimlikNo: kimlikNo.trim() }),
      });
      misafirOturum.yaz(cevap.oturum);
      await onAcildi();
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : t('gonderilemedi'));
      setGonderiliyor(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-[520px] flex-col justify-center gap-5 bg-zemin px-5 py-8 text-lacivert">
      <div className="flex flex-col gap-1.5 text-center">
        <span className="text-xs font-bold uppercase tracking-[.1em] text-marka-600">{otelAd}</span>
        <h1 className="text-[26px] font-bold tracking-[-.02em]">{t('kimlikBaslik')}</h1>
        <p className="text-sm leading-relaxed text-gri">{t('kimlikAciklama')}</p>
      </div>

      <form
        onSubmit={dogrula}
        className="flex flex-col gap-3.5 rounded-[22px] border border-cizgi bg-white p-5"
      >
        <div className="flex items-center gap-3 rounded-2xl bg-zemin px-4 py-3">
          <span className="text-xs font-semibold text-gri">{t('oda')}</span>
          <span className="text-lg font-bold">{odaNo}</span>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-slate-700">{t('kimlikNo')}</span>
          <input
            value={kimlikNo}
            onChange={(e) => setKimlikNo(e.target.value.replace(/[^0-9A-Za-z]/g, '').toUpperCase())}
            inputMode="text"
            autoComplete="off"
            maxLength={20}
            autoFocus
            aria-invalid={Boolean(hata)}
            className="h-14 w-full rounded-xl border border-slate-300 bg-white px-4 text-center text-xl font-bold tracking-[.12em] tabular-nums focus:border-marka-500 focus:outline-none focus:ring-2 focus:ring-marka-100"
          />
        </label>

        {hata && <p className="text-sm text-red-600">{hata}</p>}

        <button
          type="submit"
          disabled={gonderiliyor || kimlikNo.trim().length < 5}
          className="rounded-full bg-marka-600 px-4 py-[15px] text-[15px] font-bold text-white disabled:opacity-50"
        >
          {gonderiliyor ? t('dogrulaniyor') : t('devamEt')}
        </button>

        <p className="text-center text-xs leading-relaxed text-[#8a93ad]">{t('kimlikNeden')}</p>
      </form>

      <button
        onClick={() => onDilDegis(dil === 'tr' ? 'en' : 'tr')}
        className="self-center rounded-full border border-cizgi bg-white px-4 py-2 text-sm font-bold text-gri"
      >
        {dil === 'tr' ? 'English' : 'Türkçe'}
      </button>
    </div>
  );
}

/** Tasarimdaki ikinci telefon: talep takibi. */
function TalepListesi({
  talepler,
  odaNo,
  whatsapp,
  dil,
  t,
  onPuan,
  onIptal,
}: {
  talepler: Talep[];
  odaNo: string;
  whatsapp: string | null;
  dil: TalepDili;
  t: Ceviri;
  onPuan: (id: string, puan: number) => void;
  onIptal: (id: string) => void;
}) {
  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-semibold text-gri">
            {t('oda')} {odaNo}
          </span>
          <h2 className="text-2xl font-bold tracking-[-.02em]">{t('taleplerim')}</h2>
        </div>
        <span className="flex items-center gap-1.5 rounded-full bg-yesil-zemin px-2.5 py-1.5 text-xs font-bold text-yesil">
          <span className="relative size-[7px]">
            <span className="absolute inset-0 rounded-full bg-yesil" />
            <span className="absolute inset-0 rounded-full border-[1.5px] border-yesil [animation:ring_1.8s_ease-out_infinite]" />
          </span>
          {t('canli')}
        </span>
      </div>

      {talepler.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-[22px] border border-cizgi bg-white px-5 py-9 text-center">
          <svg width="120" height="90" viewBox="0 0 120 90" fill="none" stroke="#1f4fd8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="[animation:bob_4s_ease-in-out_infinite]" aria-hidden="true">
            <rect x="20" y="20" width="80" height="54" rx="10" fill="#edf2ff" />
            <path d="M34 38h30M34 50h44M34 62h20" stroke="#8fb0ff" />
            <circle cx="96" cy="22" r="10" fill="#f2b632" stroke="none" />
            <path d="M92 22l3 3 5-6" stroke="#0b1530" />
          </svg>
          <span className="text-base font-bold">{t('talepYok')}</span>
          <span className="text-[13px] leading-relaxed text-gri">{t('talepYokAciklama')}</span>
        </div>
      ) : (
        talepler.map((x) => (
          <TalepKarti key={x.id} talep={x} dil={dil} t={t} onPuan={onPuan} onIptal={onIptal} />
        ))
      )}

      {whatsapp && (
        <a
          href={`https://wa.me/${whatsapp.replace(/\D/g, '')}`}
          target="_blank"
          rel="noreferrer"
          className="mt-2 flex items-center gap-3 rounded-[20px] bg-lacivert p-4 text-white"
        >
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="#8fb0ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-none" aria-hidden="true">
            <rect x="6" y="10" width="28" height="22" rx="5" />
            <path d="M6 16h28M14 24h4M22 24h4" strokeDasharray="3 3" className="[animation:dash_.8s_linear_infinite]" />
          </svg>
          <span className="flex flex-1 flex-col gap-px">
            <span className="text-sm font-bold">{t('resepsiyonaYaz')}</span>
            <span className="text-xs text-[#b6c2e2]">WhatsApp</span>
          </span>
          <span className="text-lg text-mavi-acik" aria-hidden="true">
            &rsaquo;
          </span>
        </a>
      )}
    </>
  );
}

function TalepKarti({
  talep,
  dil,
  t,
  onPuan,
  onIptal,
}: {
  talep: Talep;
  dil: TalepDili;
  t: Ceviri;
  onPuan: (id: string, puan: number) => void;
  onIptal: (id: string) => void;
}) {
  const renk = RENK[talep.tip];
  const hap = DURUM_HAP[talep.durum];
  const adim = TALEP_ADIMI[talep.durum];
  const metin = talepMetni(talep.tip, dil);
  const secenek = secenekBul(talep.tip, talep.secenek);
  const iptal = talep.durum === 'IPTAL';
  const durumMetni =
    talep.durum === 'ALINDI'
      ? t('alindi')
      : talep.durum === 'ISLEMDE'
        ? metin.ortaAdim
        : talep.durum === 'TAMAMLANDI'
          ? t('tamamlandi')
          : t('iptalEdildi');

  return (
    <div
      className={
        'flex flex-col gap-3 rounded-[22px] border border-cizgi bg-white p-4 [animation:popin_.4s_cubic-bezier(.2,.8,.2,1)_both] ' +
        (iptal ? 'opacity-60' : '')
      }
    >
      <div className="flex items-center gap-3">
        <span
          className="grid size-10 flex-none place-items-center rounded-xl"
          style={{ background: renk.zemin, color: renk.yazi }}
        >
          <Ikon yol={MISAFIR_TALEPLERI[talep.tip].ikon} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-px">
          <span className="text-[15px] font-bold">{metin.baslik}</span>
          <span className="truncate text-xs text-gri">
            {secenek ? secenek[dil] : talep.secenek}
            {talep.ucretKurus > 0 && ` · ${paraEtiketi(talep.ucretKurus)}`} · {saat(talep.an)}
          </span>
        </div>
        <span
          className="flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold"
          style={{ background: hap.zemin, color: hap.yazi }}
        >
          {adim >= 0 && adim < 2 && (
            <svg width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.2" className="[animation:spin_.9s_linear_infinite]" aria-hidden="true">
              <path d="M7 1.5a5.5 5.5 0 1 1-5.5 5.5" />
            </svg>
          )}
          {durumMetni}
        </span>
      </div>

      {!iptal && (
        <>
          <div className="flex items-center">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex min-w-0 flex-1 items-center last:flex-none">
                <span
                  className="grid size-[22px] flex-none place-items-center rounded-full border-2 transition-colors duration-300"
                  style={{
                    background: adim >= i ? '#1f4fd8' : '#fff',
                    borderColor: adim >= i ? '#1f4fd8' : '#dfe6f5',
                  }}
                >
                  {adim >= i && (
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M2.5 6l2.5 2.5 4.5-5" />
                    </svg>
                  )}
                </span>
                {i < 2 && (
                  <span
                    className="h-0.5 flex-1 transition-colors duration-300"
                    style={{ background: adim > i ? '#1f4fd8' : '#e6ebf7' }}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[11px] font-semibold text-[#8a93ad]">
            <span>{t('alindi')}</span>
            <span>{metin.ortaAdim}</span>
            <span>{t('tamamlandi')}</span>
          </div>
        </>
      )}

      {talep.durum === 'ISLEMDE' && (
        <div className="flex items-center gap-2 rounded-xl bg-zemin px-3 py-2.5 text-[12.5px] text-gri">
          <span className="grid size-6 flex-none place-items-center rounded-full bg-[#7c3aed] text-[10px] font-bold text-white">
            {MISAFIR_TALEPLERI[talep.tip].ekip[0]}
          </span>
          {talep.tip === 'GEC_CIKIS'
            ? t('resepsiyonBakiyor')
            : t('gorevliYolda').replace('{dk}', String(MISAFIR_TALEPLERI[talep.tip].sureDk))}
        </div>
      )}

      {/* Henuz isleme alinmamis talep geri alinabilir; alinmis is alinamaz. */}
      {talep.durum === 'ALINDI' && (
        <button
          onClick={() => onIptal(talep.id)}
          className="self-start rounded-full border-[1.5px] border-cizgi px-3 py-1.5 text-xs font-semibold text-gri transition-colors hover:border-gri hover:text-lacivert"
        >
          {t('talebiGeriAl')}
        </button>
      )}

      {talep.durum === 'TAMAMLANDI' && (
        <div className="flex items-center justify-center gap-1.5 pt-0.5">
          <span className="mr-1 text-xs text-gri">{t('nasildi')}</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => onPuan(talep.id, n)}
              aria-label={`${n}`}
              className="p-0.5 transition-transform active:scale-125"
              style={{ color: (talep.puan ?? 0) >= n ? '#f2b632' : '#dfe6f5' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2l3 6.5 7 .8-5.2 4.8 1.4 7L12 17.6 5.8 21l1.4-7L2 9.3l7-.8z" />
              </svg>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Check-out olmus ya da hic konaklama olmayan oda.
 * Tasarimin "Link check-out'ta kendiliğinden kapanır" vaadinin karsiligi;
 * odada konaklayan OLUP OLMADIGINI ele vermeden ayni metni gosterir.
 */
function LinkKapali({ t }: { t: Ceviri }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-zemin p-6">
      <div className="flex max-w-[34ch] flex-col items-center gap-3 rounded-[22px] border border-cizgi bg-white px-6 py-9 text-center">
        <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="#8fb0ff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4" y="10" width="16" height="10" rx="3" />
          <path d="M8 10V7a4 4 0 018 0" />
        </svg>
        <h1 className="text-lg font-bold text-lacivert">{t('linkKapali')}</h1>
        <p className="text-sm leading-relaxed text-gri">{t('linkKapaliAciklama')}</p>
      </div>
    </div>
  );
}
